// Global date-year guard.
//
// <input type="date"> stores "0026-08-25" (not "2026-08-25") when a user types
// just "26" into the year field. These helpers rewrite any committed 2-digit year
// to 2000+year and push the corrected value back through React's native setter, so
// every controlled date field across the app persists a 4-digit 20xx year.

/**
 * Pure transform: normalise a YYYY-MM-DD string so a 2-digit year becomes 20xx.
 * Leaves already-valid (3-4 digit) years and non-date strings untouched.
 */
export function normalizeDateYear(value: string): string {
  const m = /^(\d{1,4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return value;
  const year = parseInt(m[1], 10);
  if (year >= 100) return value; // already 3-4 digit — leave it
  return `${String(year + 2000).padStart(4, '0')}-${m[2]}-${m[3]}`;
}

/**
 * Install a document-wide guard that corrects the year on any <input type="date">.
 *
 * It runs on `focusout` — i.e. only once the user leaves the whole field — NOT on
 * `change`/`input`, because native date inputs fire those mid-edit (the moment a
 * partial year like "2" pads to a valid "0002"), and rewriting the value then
 * resets the segment cursor and blocks further typing. Waiting for focusout lets
 * the user finish typing normally, then normalises the committed value.
 *
 * Returns a cleanup function that removes the listener.
 */
export function installDateYearGuard(): () => void {
  const reactValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype, 'value',
  )?.set;

  const fixDateYear = (el: HTMLInputElement) => {
    if (el.type !== 'date' || !el.value) return;
    const fixed = normalizeDateYear(el.value);
    if (fixed === el.value) return;
    // Push through the native setter + input event so React's onChange sees it.
    reactValueSetter?.call(el, fixed);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  };

  // `focusout` bubbles (unlike `blur`), so one document-level listener covers all.
  const onFocusOut = (e: Event) => {
    if (e.target instanceof HTMLInputElement && e.target.type === 'date') {
      fixDateYear(e.target);
    }
  };
  document.addEventListener('focusout', onFocusOut);
  return () => document.removeEventListener('focusout', onFocusOut);
}
